import * as React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { BarChart } from '@mui/x-charts/BarChart';
import { useNavigate } from 'react-router-dom';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import { getAuditLogsPagedByScope, type AuditLog } from '../../api/audits';
import { useAuth } from '../../auth/AuthContext';
import { APP_PATHS, canAccessPath } from '../../auth/role-based-access-control';
import { useSetHeaderTitle } from '../../layouts/HeaderTitleContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

type ActionColor = 'success' | 'info' | 'error' | 'warning' | 'default';

const actionChipColor = (action?: string): ActionColor => {
  switch (action?.toUpperCase()) {
    case 'ADDED':   return 'success';
    case 'UPDATED': return 'info';
    case 'DELETED': return 'error';
    case 'SWAPPED': return 'warning';
    default:        return 'default';
  }
};

const formatTs = (raw?: string | null): string => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const todayIso = (): string => new Date().toISOString().slice(0, 10);

// ── StatCard ──────────────────────────────────────────────────────────────────

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  accentColor: string;
  bgColor: string;
  icon: React.ReactNode;
  loading?: boolean;
}

function StatCard({ title, value, subtitle, accentColor, bgColor, icon, loading }: StatCardProps) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 3,
        borderColor: 'divider',
        boxShadow: 'none',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 2 },
      }}
    >
      <CardContent sx={{ pb: '16px !important' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: bgColor,
              color: accentColor,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        </Stack>
        <Box sx={{ mt: 1.5 }}>
          {loading ? (
            <CircularProgress size={20} sx={{ display: 'block' }} />
          ) : (
            <Typography variant="h4" fontWeight={800} sx={{ color: 'text.primary' }}>
              {value}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.disabled">
              {subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── QuickActionTile ───────────────────────────────────────────────────────────

interface QuickActionTileProps {
  label: string;
  description: string;
  icon: React.ReactNode;
  accentColor: string;
  bgColor: string;
  badge?: number;
  disabled?: boolean;
  onClick: () => void;
}

type QuickAction = QuickActionTileProps & {
  accent: string;
  bg: string;
};

function QuickActionTile({
  label,
  description,
  icon,
  accentColor,
  bgColor,
  badge,
  disabled = false,
  onClick,
}: QuickActionTileProps) {
  return (
    <Paper
      variant="outlined"
      onClick={disabled ? undefined : onClick}
      sx={{
        p: 1.5,
        cursor: disabled ? 'not-allowed' : 'pointer',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        borderRadius: 2,
        transition: 'all 0.15s ease',
        opacity: disabled ? 0.55 : 1,
        '&:hover': disabled ? undefined : {
          bgcolor: 'action.hover',
          borderColor: accentColor,
          boxShadow: 2,
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: bgColor,
          color: accentColor,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="body2" fontWeight={700} noWrap>
            {label}
          </Typography>
          {badge !== undefined && badge > 0 && (
            <Chip
              label={badge}
              size="small"
              color="warning"
              sx={{
                height: 18,
                '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem', fontWeight: 700 },
              }}
            />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" noWrap>
          {description}
        </Typography>
      </Box>
      <ArrowForwardIcon sx={{ fontSize: 16, color: 'action.disabled', flexShrink: 0 }} />
    </Paper>
  );
}

// ── DashboardPage ─────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, realmRoles } = useAuth();
  useSetHeaderTitle(
    'Support Portal Dashboard',
    `Welcome back${user?.name ? `, ${user.name.split(' ')[0]}` : ''}`
  );

  const [logs, setLogs] = React.useState<AuditLog[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  /** MUI X Charts needs a resolved width on small viewports; measure the card content area. */
  const actionMixChartRef = React.useRef<HTMLDivElement>(null);
  const [actionMixChartWidth, setActionMixChartWidth] = React.useState<number | undefined>(undefined);
  React.useLayoutEffect(() => {
    const el = actionMixChartRef.current;
    if (!el) return;
    const update = () => {
      const w = Math.floor(el.getBoundingClientRect().width);
      if (w > 0) setActionMixChartWidth(w);
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  React.useEffect(() => {
    let mounted = true;
    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const all: AuditLog[] = [];
        for (let page = 0; page < 3; page++) {
          const res = await getAuditLogsPagedByScope('contact', { page, size: 200 });
          if (!res.success || !res.data) {
            if (mounted && page === 0) {
              setError(res.message ?? 'Could not load dashboard analytics.');
            }
            break;
          }
          all.push(...(res.data.logs ?? []));
          if (page + 1 >= (res.data.totalPages ?? 1)) break;
        }
        if (mounted) setLogs(all);
      } catch {
        if (mounted) setError('Could not load dashboard analytics. Check your connection.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchLogs();
    return () => { mounted = false; };
  }, []);

  // ── Derived analytics ──────────────────────────────────────────────────────

  const analytics = React.useMemo(() => {
    const now = new Date();
    const todayMidnight = new Date(now);
    todayMidnight.setHours(0, 0, 0, 0);
    const weekStart = new Date(todayMidnight);
    weekStart.setDate(todayMidnight.getDate() - 6);
    const h24ago = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const actionMix: Record<string, number> = { ADDED: 0, UPDATED: 0, DELETED: 0, SWAPPED: 0 };
    let totalToday = 0;
    let totalThisWeek = 0;
    let deletesThisWeek = 0;
    const clientsToday = new Set<string>();
    const clientLogs24h = new Map<string, AuditLog[]>();

    for (const log of logs) {
      const d = new Date(log.createdOn);
      const action = log.action?.toUpperCase() ?? '';

      if (d >= todayMidnight) {
        totalToday++;
        if (log.externalClientId) clientsToday.add(String(log.externalClientId));
      }

      if (d >= weekStart) {
        totalThisWeek++;
        if (action === 'DELETED') deletesThisWeek++;
        if (action in actionMix) actionMix[action]++;
      }

      if (d >= h24ago && log.externalClientId) {
        const k = String(log.externalClientId);
        if (!clientLogs24h.has(k)) clientLogs24h.set(k, []);
        clientLogs24h.get(k)!.push(log);
      }
    }

    const repeatChanges = Array.from(clientLogs24h.entries())
      .filter(([, v]) => v.length >= 3)
      .map(([clientId, entries]) => ({
        clientId,
        clientName: entries[0].clientFirstName ?? '—',
        count: entries.length,
        lastAction: entries[0].action,
        lastAgent: entries[0].createdBy,
        lastTime: entries[0].createdOn,
      }))
      .sort((a, b) => b.count - a.count);

    return {
      totalToday,
      totalThisWeek,
      deletesThisWeek,
      uniqueClientsToday: clientsToday.size,
      last10: logs.slice(0, 10),
      actionMix,
      repeatChanges,
    };
  }, [logs]);

  const {
    totalToday,
    totalThisWeek,
    deletesThisWeek,
    uniqueClientsToday,
    last10,
    actionMix,
    repeatChanges,
  } = analytics;

  // ── Colour tokens ──────────────────────────────────────────────────────────

  const colors = {
    primary: { main: theme.palette.primary.main,  bg: alpha(theme.palette.primary.main,  0.1) },
    success: { main: theme.palette.success.main,  bg: alpha(theme.palette.success.main,  0.1) },
    info:    { main: theme.palette.info.main,     bg: alpha(theme.palette.info.main,     0.1) },
    error:   { main: theme.palette.error.main,    bg: alpha(theme.palette.error.main,    0.1) },
    warning: { main: theme.palette.warning.main,  bg: alpha(theme.palette.warning.main,  0.1) },
  };

  const canAccessClientLookup = canAccessPath(realmRoles, APP_PATHS.CLIENT_LOOKUP);
  const canAccessAuditLogs = canAccessPath(realmRoles, APP_PATHS.AUDIT_LOGS);
  const canAccessContactChangesLogs = canAccessPath(realmRoles, APP_PATHS.CLIENT_CHANGES_LOG);
  const canAccessReconciliation = canAccessPath(realmRoles, APP_PATHS.RECONCILIATION);
  const canAccessBulkSms = canAccessPath(realmRoles, APP_PATHS.BULK_SMS);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Title now lives in the shared Header bar via useSetHeaderTitle above */}
      <Box sx={{ mb: 3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── KPI Stat Cards ──────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Actions Today"
            value={totalToday}
            subtitle="Since midnight"
            accentColor={colors.primary.main}
            bgColor={colors.primary.bg}
            icon={<CalendarTodayIcon />}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Actions This Week"
            value={totalThisWeek}
            subtitle="Last 7 days"
            accentColor={colors.info.main}
            bgColor={colors.info.bg}
            icon={<HistoryRoundedIcon />}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Deletes This Week"
            value={deletesThisWeek}
            subtitle="Review for compliance"
            accentColor={colors.error.main}
            bgColor={colors.error.bg}
            icon={<DeleteForeverIcon />}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Clients Touched Today"
            value={uniqueClientsToday}
            subtitle="Unique FXBO IDs"
            accentColor={colors.warning.main}
            bgColor={colors.warning.bg}
            icon={<ManageAccountsIcon />}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* ── Quick Actions ────────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          Quick Actions
        </Typography>
        <Grid container spacing={1.5}>
          {([
            canAccessClientLookup
              ? {
                  label: 'Client Lookup',
                  description: 'Search and manage client contacts',
                  icon: <SearchRoundedIcon fontSize="small" />,
                  accent: colors.primary.main,
                  bg: colors.primary.bg,
                  onClick: () => navigate(APP_PATHS.CLIENT_LOOKUP),
                }
              : null,
            canAccessContactChangesLogs
              ? {
                  label: 'Contact Changes Audit Logs',
                  description: 'Review contact updates activity',
                  icon: <HistoryRoundedIcon fontSize="small" />,
                  accent: colors.info.main,
                  bg: colors.info.bg,
                  onClick: () => navigate(APP_PATHS.CLIENT_CHANGES_LOG),
                }
              : null,
            canAccessReconciliation
              ? {
                  label: 'Reconciliation',
                  description: 'Open reconsiliation workspace',
                  icon: <AccountBalanceWalletRoundedIcon fontSize="small" />,
                  accent: colors.success.main,
                  bg: colors.success.bg,
                  onClick: () => navigate(APP_PATHS.RECONCILIATION),
                }
              : null,
            canAccessBulkSms
              ? {
                  label: 'Bulk SMS',
                  description: 'Send a broadcast message to a client list',
                  icon: <CampaignRoundedIcon fontSize="small" />,
                  accent: colors.primary.main,
                  bg: colors.primary.bg,
                  onClick: () => navigate(APP_PATHS.BULK_SMS),
                }
              : null,
            canAccessAuditLogs
              ? {
                  label: "Today's Deletes",
                  description: 'DELETED actions from today',
                  icon: <DeleteForeverIcon fontSize="small" />,
                  accent: colors.error.main,
                  bg: colors.error.bg,
                  onClick: () =>
                    navigate(APP_PATHS.AUDIT_LOGS, {
                      state: { initialAction: 'DELETED', initialDate: todayIso() },
                    }),
                }
              : null,
            canAccessAuditLogs
              ? {
                  label: 'My Actions Today',
                  description: `Agent: ${user?.email ?? 'you'} · today`,
                  icon: <ManageAccountsIcon fontSize="small" />,
                  accent: colors.success.main,
                  bg: colors.success.bg,
                  onClick: () =>
                    navigate(APP_PATHS.AUDIT_LOGS, {
                      state: {
                        initialAgent: user?.email ?? '',
                        initialDate: todayIso(),
                      },
                    }),
                }
              : null,
            canAccessAuditLogs
              ? {
                  label: 'Actions Needing Review',
                  description: 'Clients changed 3+ times in last 24h',
                  icon: <ReportProblemIcon fontSize="small" />,
                  accent: repeatChanges.length > 0 ? colors.warning.main : theme.palette.action.active,
                  bg: repeatChanges.length > 0
                    ? colors.warning.bg
                    : alpha(theme.palette.action.active, 0.1),
                  badge: repeatChanges.length > 0 ? repeatChanges.length : undefined,
                  onClick: () => {
                    const el = document.getElementById('repeat-changes-anchor');
                    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  },
                }
              : null,
          ] as Array<QuickAction | null>)
            .filter((action): action is QuickAction => action !== null)
            .map(({ label, description, icon, accent, bg, badge, onClick }) => (
            <Grid key={label} size={{ xs: 12, sm: 6, md: 4 }} sx={{ minWidth: 0 }}>
              <QuickActionTile
                label={label}
                description={description}
                icon={icon}
                accentColor={accent}
                bgColor={bg}
                badge={badge}
                onClick={onClick}
              />
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* ── Action Mix — This Week ───────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ mb: 3, width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
        <CardContent sx={{ px: { xs: 1, sm: 2 }, pt: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Action Mix — This Week
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box
              ref={actionMixChartRef}
              sx={{
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'hidden',
              }}
            >
              {actionMixChartWidth && actionMixChartWidth > 0 ? (
                <BarChart
                  layout="horizontal"
                  width={actionMixChartWidth}
                  height={220}
                  sx={{ width: '100%', maxWidth: '100%', display: 'block' }}
                  yAxis={[{
                    scaleType: 'band',
                    data: ['ADDED', 'UPDATED', 'DELETED', 'SWAPPED'],
                    categoryGapRatio: 0.45,
                    /**
                     * Label column width only. Do not mirror this in margin.left — MUI adds both, so
                     * duplicate values double the empty space on the left.
                     */
                    width: isNarrow ? 86 : 112,
                    tickLabelStyle: { fontSize: isNarrow ? 10 : 11 },
                  }]}
                  xAxis={[{ tickLabelStyle: { fontSize: isNarrow ? 10 : 11 } }]}
                  series={[{
                    data: [
                      actionMix['ADDED']   ?? 0,
                      actionMix['UPDATED'] ?? 0,
                      actionMix['DELETED'] ?? 0,
                      actionMix['SWAPPED'] ?? 0,
                    ],
                    label: 'Actions',
                    color: colors.primary.main,
                  }]}
                  margin={{
                    left: isNarrow ? 4 : 16,
                    right: isNarrow ? 8 : 24,
                    top: 8,
                    bottom: 24,
                  }}
                  grid={{ vertical: true }}
                  hideLegend
                />
              ) : (
                <Box sx={{ height: 220 }} aria-hidden />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Latest Activity Feed ─────────────────────────────────────────────── */}
      {canAccessAuditLogs && (
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1.5 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <HistoryRoundedIcon fontSize="small" color="action" />
            <Typography variant="subtitle1" fontWeight={700}>
              Latest Activity
            </Typography>
            <Chip
              label="Last 10 actions"
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          </Stack>
          <Button
            size="small"
            variant="text"
            onClick={() => navigate('/audit-logs')}
            endIcon={<ArrowForwardIcon fontSize="small" />}
          >
            View all
          </Button>
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : last10.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No recent activity found.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>FXBO ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Entity</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Agent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {last10.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell
                      sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.75rem' }}
                    >
                      {formatTs(log.createdOn)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {log.externalClientId ?? '—'}
                    </TableCell>
                    <TableCell>{log.clientFirstName ?? '—'}</TableCell>
                    <TableCell>{log.entityType ?? '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={log.action ?? '—'}
                        size="small"
                        color={actionChipColor(log.action)}
                        variant="outlined"
                        sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell>{log.createdBy?.trim() || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>
      )}

      {/* ── Repeat Changes Alert ─────────────────────────────────────────────── */}
      <Paper
        id="repeat-changes-anchor"
        variant="outlined"
        sx={{
          p: 2,
          ...(repeatChanges.length > 0 && {
            borderColor: 'warning.main',
            borderWidth: '1.5px',
          }),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <WarningAmberRoundedIcon
            fontSize="small"
            color={repeatChanges.length > 0 ? 'warning' : 'action'}
          />
          <Typography variant="subtitle1" fontWeight={700}>
            Repeat Changes
          </Typography>
          <Chip
            label="Last 24h"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
          {repeatChanges.length > 0 && (
            <Chip
              label={`${repeatChanges.length} client${repeatChanges.length > 1 ? 's' : ''} flagged`}
              size="small"
              color="warning"
              sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700 }}
            />
          )}
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Clients with 3 or more changes in the last 24 hours — may warrant review
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : repeatChanges.length === 0 ? (
          <Alert severity="success">
            No clients with 3+ changes in the last 24 hours — looking good.
          </Alert>
        ) : (
          <Stack spacing={0} divider={<Divider />}>
            {repeatChanges.map(({ clientId, clientName, count, lastAction, lastAgent, lastTime }) => (
              <Box key={clientId} sx={{ py: 1.25 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                        FXBO {clientId}
                      </Typography>
                      {clientName !== '—' && (
                        <Typography variant="body2" color="text.secondary">
                          ({clientName})
                        </Typography>
                      )}
                      <Chip
                        label={`${count}×`}
                        size="small"
                        color={count >= 5 ? 'error' : 'warning'}
                        sx={{ fontWeight: 800, height: 20, fontSize: '0.7rem' }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Last:{' '}
                      <Chip
                        label={lastAction ?? '—'}
                        size="small"
                        color={actionChipColor(lastAction)}
                        variant="outlined"
                        sx={{ height: 16, fontSize: '0.65rem', fontWeight: 600, mr: 0.5 }}
                      />
                      by {lastAgent?.trim() || '?'} · {formatTs(lastTime)}
                    </Typography>
                  </Box>
                  <Tooltip title="Open in Client Lookup">
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      sx={{ flexShrink: 0, fontSize: '0.7rem', py: 0.25, whiteSpace: 'nowrap' }}
                      onClick={() =>
                        navigate('/client-lookup', { state: { initialQuery: clientId } })
                      }
                    >
                      View client
                    </Button>
                  </Tooltip>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </>
  );
}
